// Premium Client Booking History Report — Enterprise Edition (matches Owner Receipt tier)
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { deriveContractMoneyBreakdown } from '../contractBookingDisplay';
import {
  BRAND, rgb, fmtINR, fmtDateTime, computeDocHash,
  roundRectFill, roundRectFS, drawDiamond, drawStatusPill,
} from './shared';
import { formatHoursHm } from '../durationHours';
import { computeBookingTaxQuote } from '../gst/gstRules';

const isContractReportRow = (b) => (
  String(b?.raw?.booking_type || b?.type || '').toLowerCase() === 'contract'
);

const COMPANY = {
  name: 'DEVELOPMENT EXPRESS',
  tagline: 'Development Express · Enterprise Edition',
  addr: 'Karad, Satara, Maharashtra - 415110',
  phone: '+91-8408000084',
  email: 'om.chavan2026@zohomail.in',
  web: 'developmentexpress.in',
};

const PDF_FOOTER_TAG = 'DE Premium PDF · v2026-05-30';

const MARGIN = { top: 25, bottom: 20, left: 15, right: 15 };
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN.left - MARGIN.right; // 180mm

const statusColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('cancel')) return { bg: BRAND.RUBY_LT, fg: BRAND.RUBY };
  if (s.includes('complete') || s.includes('done')) return { bg: BRAND.EMERALD_LT, fg: BRAND.EMERALD };
  if (s.includes('active') || s.includes('pending')) return { bg: BRAND.SAPPHIRE_LT, fg: BRAND.SAPPHIRE };
  return { bg: BRAND.AMBER_LT, fg: BRAND.AMBER };
};

const drawCoverPage = (doc, data) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  doc.setFillColor(...rgb(BRAND.COVER_BG));
  doc.rect(0, 0, w, h, 'F');

  doc.setDrawColor(31, 27, 18);
  doc.setLineWidth(0.1);
  for (let i = 0; i < h; i += 2.5) {
    doc.line(0, i, w, i + 1);
  }

  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.5);
  [[15, 15, 15, 32, 32, 15], [w - 15, 15, w - 15, 32, w - 32, 15],
    [15, h - 15, 15, h - 32, 32, h - 15], [w - 15, h - 15, w - 15, h - 32, w - 32, h - 15]]
    .forEach(([x1, y1, x2, y2, x3, y3]) => {
      doc.line(x1, y1, x2, y2);
      doc.line(x1, y1, x3, y3);
    });

  const emblemY = 52;
  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.6);
  doc.circle(w / 2, emblemY, 18, 'S');
  doc.setLineWidth(0.25);
  doc.circle(w / 2, emblemY, 14, 'S');
  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('DE', w / 2, emblemY + 2, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.text('SINCE 2011', w / 2, emblemY + 8, { align: 'center' });

  const bandY = h - 188;
  doc.setFillColor(...rgb(BRAND.COVER_GOLD));
  doc.rect(0, bandY, w, 0.3, 'F');
  doc.rect(0, bandY + 28, w, 0.3, 'F');

  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(COMPANY.name, w / 2, bandY + 10, { align: 'center' });
  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('M A C H I N E   O S   ·   C L I E N T   R E P O R T', w / 2, bandY + 17, { align: 'center' });

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('O F F I C I A L   B O O K I N G   H I S T O R Y', w / 2, bandY + 38, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('Booking History', w / 2, bandY + 52, { align: 'center' });
  doc.text('Report', w / 2, bandY + 62, { align: 'center' });

  const dy = bandY + 74;
  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.4);
  doc.line(w / 2 - 30, dy, w / 2 - 4, dy);
  doc.line(w / 2 + 4, dy, w / 2 + 30, dy);
  drawDiamond(doc, w / 2, dy, 1.5, BRAND.COVER_GOLD);

  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Total Spent (incl. GST)', w / 2, dy + 12, { align: 'center' });
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text(fmtINR(data.totalSpent), w / 2, dy + 24, { align: 'center' });

  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const coverParts = [
    `${data.bookingCount} bookings`,
    formatHoursHm(data.totalHours),
  ];
  if (Number(data.totalCycles || 0) > 0) {
    const c = Number(data.totalCycles);
    coverParts.push(`${c} ${c === 1 ? 'cycle' : 'cycles'}`);
  }
  doc.text(coverParts.join('  ·  '), w / 2, dy + 34, { align: 'center' });

  const infoY = h - 42;
  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('REPORT ID', 20, infoY);
  doc.text('CLIENT', w / 2, infoY, { align: 'center' });
  doc.text('GENERATED', w - 20, infoY, { align: 'right' });

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(data.reportId, 20, infoY + 5);
  doc.text(data.clientName, w / 2, infoY + 5, { align: 'center' });
  doc.text(data.generatedOn, w - 20, infoY + 5, { align: 'right' });

  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.text(`${COMPANY.addr}  ·  ${COMPANY.phone}`, w / 2, h - 18, { align: 'center' });
};

const drawInsideHeaderFooter = (doc, page, totalPages, reportId, docHash) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const left = MARGIN.left;
  const right = w - MARGIN.right;

  doc.setFillColor(...rgb(BRAND.GOLD));
  doc.rect(0, 0, w, 4, 'F');
  doc.setFillColor(...rgb(BRAND.GOLD_DEEP));
  doc.rect(0, 4, w, 0.5, 'F');

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('DEVELOPMENT EXPRESS', left, 11);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('Development Express  ·  Client Booking Report', left, 15);

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Booking History - Detail', w / 2, 13, { align: 'center' });

  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text('OFFICIAL  ·  REPORT', right, 11, { align: 'right' });
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.text(reportId, right, 15, { align: 'right' });

  doc.setDrawColor(...rgb(BRAND.BORDER));
  doc.setLineWidth(0.2);
  doc.line(left, 18, right, 18);

  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.4);
  doc.line(left, h - 16, w / 2 - 4, h - 16);
  doc.line(w / 2 + 4, h - 16, right, h - 16);
  drawDiamond(doc, w / 2, h - 16, 1.3, BRAND.GOLD);

  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text(`${COMPANY.web}  ·  ${COMPANY.email}`, left, h - 11);
  doc.text('(c) 2026 Development Express. All rights reserved.', w / 2, h - 11, { align: 'center' });
  doc.text(`Page ${page} of ${totalPages}`, right, h - 11, { align: 'right' });
  doc.setTextColor(...rgb(BRAND.MUTED_LT));
  doc.setFontSize(5.5);
  doc.text(`${PDF_FOOTER_TAG} · ${docHash.substring(0, 12)}`, left, h - 6);
  doc.text(`Generated ${fmtDateTime(new Date().toISOString())}`, right, h - 6, { align: 'right' });
};

const drawKPICard = (doc, x, y, cw, ch, label, value, sub, accent) => {
  roundRectFill(doc, x + 0.4, y + 0.4, cw, ch, 1.5, '#EFEFEF');
  roundRectFS(doc, x, y, cw, ch, 1.5, '#FFFFFF', BRAND.BORDER, 0.2);
  doc.setFillColor(...rgb(accent));
  doc.rect(x, y, cw, 1, 'F');
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.text(label.toUpperCase(), x + 3, y + 5);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(value, x + 3, y + 12);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  const subLines = doc.splitTextToSize(String(sub || ''), cw - 6);
  doc.text(subLines[0], x + 3, y + 17);
};

/**
 * @param {Array<{id,date,machine,type,hours,baseAmt,gst,total,status}>} bookings
 * @param {string} clientName
 */
export async function generatePremiumClientBookingReport(bookings = [], clientName = 'Client', options = {}) {
  const { download = true } = options;
  const rows = Array.isArray(bookings) ? bookings : [];
  const totalBase = rows.reduce((a, b) => a + Number(b.baseAmt || b.amount || 0), 0);
  const totalGST = rows.reduce((a, b) => a + Number(b.gst ?? computeBookingTaxQuote(b.baseAmt || 0).gst), 0);
  const totalSpent = rows.reduce((a, b) => a + Number(b.total || 0), 0);
  // Spot hours only — contract row.hours is cycle quantity (never mix into hours).
  const totalHours = rows.reduce((a, b) => (
    isContractReportRow(b) ? a : a + Number(b.hours || 0)
  ), 0);
  const totalCycles = rows.reduce((a, b) => (
    isContractReportRow(b) ? a + Number(b.hours || 0) : a
  ), 0);
  const bookingCount = rows.length;
  const completedCount = rows.filter((b) => String(b.status).toLowerCase().includes('complete')).length;

  const year = new Date().getFullYear();
  const reportId = `DE/RPT/CLI/${year}/${String(bookingCount).padStart(4, '0')}`;
  const generatedOn = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const docHash = await computeDocHash(reportId, clientName, totalSpent);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth() || PAGE_W;
  const contentW = (w - MARGIN.left - MARGIN.right) || CONTENT_W;

  drawCoverPage(doc, {
    reportId,
    clientName,
    generatedOn,
    totalSpent,
    bookingCount,
    totalHours,
    totalCycles,
  });

  doc.addPage();
  let y = MARGIN.top;

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Booking Summary', MARGIN.left, y);
  drawStatusPill(doc, w - MARGIN.right - 28, y - 4, 28, 6, `${bookingCount} Bookings`, BRAND.GOLD_PALE, BRAND.GOLD_DARK);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(`Client: ${clientName}  ·  Period: All active bookings on account`, MARGIN.left, y + 5);
  y += 12;

  const cardW = (contentW - 12) / 4;
  drawKPICard(doc, MARGIN.left, y, cardW, 20, 'Bookings', String(bookingCount), `${completedCount} completed`, BRAND.GOLD);
  // Contract-only accounts: replace hrs card with cycles (never mislabel quantity as hours).
  if (totalCycles > 0 && totalHours === 0) {
    drawKPICard(
      doc,
      MARGIN.left + cardW + 4,
      y,
      cardW,
      20,
      'Contract Cycles',
      `${totalCycles} ${totalCycles === 1 ? 'cycle' : 'cycles'}`,
      'Not machine hours',
      BRAND.SAPPHIRE,
    );
  } else {
    drawKPICard(
      doc,
      MARGIN.left + cardW + 4,
      y,
      cardW,
      20,
      'Machine Hrs',
      formatHoursHm(totalHours),
      totalCycles > 0
        ? `${totalCycles} ${totalCycles === 1 ? 'cycle' : 'cycles'} on contracts`
        : 'Logged on spot bookings',
      BRAND.SAPPHIRE,
    );
  }
  drawKPICard(doc, MARGIN.left + (cardW + 4) * 2, y, cardW, 20, 'Taxable', fmtINR(totalBase), 'Base before GST', BRAND.AMBER);
  drawKPICard(doc, MARGIN.left + (cardW + 4) * 3, y, cardW, 20, 'Total Spent', fmtINR(totalSpent), `GST ${fmtINR(totalGST)}`, BRAND.EMERALD);
  y += 26;

  autoTable(doc, {
    startY: y,
    head: [['Booking ID', 'Date', 'Machine', 'Type', 'Hrs', 'Base', 'GST', 'Total', 'Status']],
    body: rows.map((b) => {
      const base = Number(b.baseAmt || b.amount || 0);
      const gst = Number(b.gst ?? computeBookingTaxQuote(base).gst);
      const total = Number(b.total || base + gst);
      const qty = Number(b.hours || 0);
      const contract = isContractReportRow(b);
      let baseCell = fmtINR(base);
      let gstCell = fmtINR(gst);
      if (contract) {
        const money = deriveContractMoneyBreakdown(b.raw || b);
        // Helvetica has no ₹ glyph — fmtINR uses "Rs " (same as rest of this PDF).
        baseCell = `Hire (A): ${fmtINR(money.totalHireValueA)}`;
        gstCell = `Mob (B): ${fmtINR(money.totalMobilizationB)}`;
      }
      return [
        String(b.id || '—'),
        String(b.date || '—'),
        String(b.machine || b.machineName || '—'),
        String(b.type || '—'),
        contract
          ? `${qty} ${qty === 1 ? 'cycle' : 'cycles'}`
          : formatHoursHm(qty),
        baseCell,
        gstCell,
        fmtINR(total),
        String(b.status || '—'),
      ];
    }),
    foot: [['', '', '', '', 'TOTAL', fmtINR(totalBase), fmtINR(totalGST), fmtINR(totalSpent), '']],
    styles: {
      font: 'helvetica', fontSize: 6, cellPadding: 1.5, valign: 'middle',
      overflow: 'linebreak',
      lineColor: rgb(BRAND.BORDER), lineWidth: 0.1,
    },
    headStyles: {
      fillColor: rgb(BRAND.GOLD_PALE), textColor: rgb(BRAND.GOLD_DARK),
      fontStyle: 'bold', fontSize: 6,
    },
    footStyles: {
      fillColor: rgb(BRAND.EMERALD), textColor: [255, 255, 255],
      fontStyle: 'bold', fontSize: 6.5,
    },
    alternateRowStyles: { fillColor: rgb(BRAND.ROW_ALT) },
    columnStyles: {
      // Sum = CONTENT_W (180mm). Hrs ≥ 22mm for formatHoursHm ("93 hr 58 min").
      0: { cellWidth: 22, fontStyle: 'bold', textColor: rgb(BRAND.INK) },
      1: { cellWidth: 16 },
      2: { cellWidth: 20 },
      3: { cellWidth: 12 },
      4: { cellWidth: 22, halign: 'center' },
      5: { cellWidth: 26, halign: 'right' },
      6: { cellWidth: 22, halign: 'right', textColor: rgb(BRAND.SAPPHIRE) },
      7: { cellWidth: 22, halign: 'right', fontStyle: 'bold', textColor: rgb(BRAND.EMERALD) },
      8: { cellWidth: 18, halign: 'center' },
    },
    margin: {
      top: MARGIN.top,
      bottom: MARGIN.bottom,
      left: MARGIN.left,
      right: MARGIN.right,
    },
    didParseCell: (hook) => {
      if (hook.section === 'body' && hook.column.index === 8) {
        const st = hook.cell.raw;
        const { bg, fg } = statusColor(st);
        hook.cell.styles.fillColor = rgb(bg);
        hook.cell.styles.textColor = rgb(fg);
        hook.cell.styles.fontStyle = 'bold';
      }
      if (hook.section === 'foot' && hook.column.index >= 5 && hook.column.index <= 7) {
        hook.cell.styles.textColor = [255, 255, 255];
      }
    },
    didDrawPage: () => {
      const page = doc.internal.getCurrentPageInfo?.().pageNumber
        ?? doc.internal.getNumberOfPages();
      if (page < 2) return;
      drawInsideHeaderFooter(
        doc,
        page,
        doc.internal.getNumberOfPages(),
        reportId,
        docHash,
      );
    },
  });

  y = doc.lastAutoTable.finalY + 8;
  const pageH = doc.internal.pageSize.getHeight() || PAGE_H;
  if (y + 12 > pageH - MARGIN.bottom) {
    doc.addPage();
    y = MARGIN.top;
  }

  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text(
    'Per-booking GST invoices available from Reports tab - "GST PDF" on each eligible booking.',
    MARGIN.left,
    y,
  );
  doc.text('Wallet-only payments. Direct operator contact is prohibited per platform policy.', MARGIN.left, y + 4);

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    drawInsideHeaderFooter(doc, i, totalPages, reportId, docHash);
  }

  const safeClient = String(clientName)
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 40) || 'Client';
  const filename = `DE_Client_Booking_Report_PREMIUM_${safeClient}.pdf`;
  if (download) doc.save(filename);
  doc.__deFilename = filename;
  return doc;
}
